import { useEffect } from 'react';
import { buildBreadcrumbs, ORGANIZATION_JSONLD, WEBSITE_JSONLD } from '@/lib/seoSchemas';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Set false to skip auto-injecting Organization + BreadcrumbList. Default true. */
  includeSitewide?: boolean;
}

export default function SEOHead({
  title,
  description,
  canonical,
  ogImage = 'https://venueintel.xyz/og-image.png',
  jsonLd,
  includeSitewide = true,
}: SEOHeadProps) {
  useEffect(() => {
    // Title
    document.title = title;

    // Meta tags
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('name', 'description', description);
    setMeta('name', 'author', 'VenueIntel');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'VenueIntel');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);

    // Build @graph array
    const graph: Record<string, unknown>[] = [];

    if (includeSitewide) {
      graph.push(ORGANIZATION_JSONLD);
      graph.push(WEBSITE_JSONLD);
      graph.push(buildBreadcrumbs(canonical));
    }

    if (jsonLd) {
      if (Array.isArray(jsonLd)) {
        graph.push(...jsonLd);
      } else {
        graph.push(jsonLd);
      }
    }

    // JSON-LD
    const scriptId = 'seo-jsonld';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (graph.length > 0) {
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': graph,
      });
    }

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, [title, description, canonical, ogImage, jsonLd, includeSitewide]);

  return null;
}
